import React from "react";
import {
  Box,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from "@material-ui/core";
import {
  HaklaiStyledHeaderCell as StyledHeaderCell,
  HaklaiStyledNameHeaderCell as StyledNameHeaderCell
} from "../../shared/table/haklaiTableShared";
import {
  ACTION_COLUMN_WIDTH,
  ADD_ROW_COLUMN_WIDTH,
  DEPRECIATION_COLUMN_WIDTH,
  PTOR_COLUMN_WIDTH,
  PROPERTY_PART_NAME_COLUMN_WIDTH,
  READ_ONLY_VALUE_COLUMN_WIDTH,
  STANDARD_VALUE_COLUMN_WIDTH,
  StyledGroupCell
} from "../haklaiPropertyPartsStyles";
import {
  HAKLAI_PROPERTY_PART_LABELS,
  HAKLAI_PROPERTY_PART_TOOLTIPS
} from "../haklaiPropertyPartsTexts";

const FLOATING_TOOLBAR_SAFE_GUTTER = 48;
const PROPERTY_PARTS_TABLE_BASE_MIN_WIDTH = PROPERTY_PART_NAME_COLUMN_WIDTH
  + ADD_ROW_COLUMN_WIDTH
  + STANDARD_VALUE_COLUMN_WIDTH
  + PTOR_COLUMN_WIDTH
  + ACTION_COLUMN_WIDTH;

const getZabanColumnWidth = (hasPre1997ConstructionQuestion) => (
  hasPre1997ConstructionQuestion ? STANDARD_VALUE_COLUMN_WIDTH : READ_ONLY_VALUE_COLUMN_WIDTH
);

const getPropertyPartsTableMinWidth = ({
  showBuiltValueColumn,
  showZabanColumn,
  hasPre1997ConstructionQuestion
}) => (
  PROPERTY_PARTS_TABLE_BASE_MIN_WIDTH
  + (showBuiltValueColumn ? STANDARD_VALUE_COLUMN_WIDTH : 0)
  + (showZabanColumn ? getZabanColumnWidth(hasPre1997ConstructionQuestion) : 0)
  + DEPRECIATION_COLUMN_WIDTH
);

const getPropertyPartsColumnCount = ({ showBuiltValueColumn, showZabanColumn }) => (
  5
  + (showBuiltValueColumn ? 1 : 0)
  + (showZabanColumn ? 1 : 0)
  + 1
);

const HeaderLabel = ({ classes, children, tooltip }) => {
  const label = (
    <Typography component="span" className={classes.headerLabelText}>
      {children}
    </Typography>
  );
  if (!tooltip) return label;
  return <Tooltip title={tooltip}>{label}</Tooltip>;
};

const HaklaiPropertySection = React.memo(function HaklaiPropertySection({
  classes,
  sectionId,
  sectionTitle,
  addMode,
  addAriaLabel,
  onAddClick,
  showAddButton = true,
  bodies,
  renderRow,
  showBuiltValueColumn,
  showZabanColumn
}) {
  const columnCount = getPropertyPartsColumnCount({ showBuiltValueColumn, showZabanColumn });

  return (
    <TableBody>
      <TableRow>
        <StyledGroupCell
          colSpan={columnCount}
          className={`${classes.groupTitleCell} haklai-group-title-cell`}
        >
          <Typography component="div" dir="rtl" className={classes.groupTitleText}>
            {sectionTitle}
          </Typography>
        </StyledGroupCell>
      </TableRow>

      {bodies.map((body, bodyIndex) => {
        const rows = body.rows || [];
        if (rows.length === 0) {
          return (
            <TableRow key={`${sectionId}_${body.bodyKey}_empty`}>
              <StyledGroupCell colSpan={columnCount} className={classes.emptyStateCell}>
                <Typography component="span" className={classes.emptyStateText}>
                  {HAKLAI_PROPERTY_PART_LABELS.emptySection}
                </Typography>
              </StyledGroupCell>
            </TableRow>
          );
        }

        return (
          <React.Fragment key={`${sectionId}_${body.bodyKey}`}>
            {rows.map(([rowKey, part], rowIndex) => renderRow(rowKey, part, {
              subSectionLabel: body.subSectionLabel,
              subSectionRowSpan: rows.length,
              isFirstInSubSection: rowIndex === 0,
              isLastInBody: rowIndex === rows.length - 1,
              showAddButton: (
                showAddButton
                && bodyIndex === bodies.length - 1
                && rowIndex === rows.length - 1
              ),
              addMode,
              addAriaLabel,
              onAddClick
            }))}
          </React.Fragment>
        );
      })}
    </TableBody>
  );
});

const HaklaiPropertyPartsTable = ({
  classes,
  stickyHeaderEnabled,
  showBuiltValueColumn,
  showZabanColumn,
  hasPre1997ConstructionQuestion,
  sections
}) => {
  const zabanColumnWidth = getZabanColumnWidth(hasPre1997ConstructionQuestion);
  const tableWidth = getPropertyPartsTableMinWidth({
    showBuiltValueColumn,
    showZabanColumn,
    hasPre1997ConstructionQuestion
  });
  const tableFrameRef = React.useRef(null);
  const [isTableScrollContained, setIsTableScrollContained] = React.useState(false);

  React.useEffect(() => {
    const node = tableFrameRef.current;
    if (!node) return undefined;

    const updateScrollContainment = () => {
      const availableWidth = node.clientWidth || 0;
      const shouldContain = availableWidth > 0 && availableWidth < tableWidth + FLOATING_TOOLBAR_SAFE_GUTTER;
      setIsTableScrollContained((prev) => (prev === shouldContain ? prev : shouldContain));
    };

    updateScrollContainment();
    const ResizeObserverImpl = window.ResizeObserver;
    const resizeObserver = ResizeObserverImpl ? new ResizeObserverImpl(updateScrollContainment) : null;
    if (resizeObserver) resizeObserver.observe(node);
    window.addEventListener("resize", updateScrollContainment);

    return () => {
      if (resizeObserver) resizeObserver.disconnect();
      window.removeEventListener("resize", updateScrollContainment);
    };
  }, [tableWidth]);

  return (
    <Box ref={tableFrameRef} className={classes.tableFrameAlign}>
      <TableContainer
        className={[
          classes.tableWrapper,
          isTableScrollContained ? classes.tableWrapperScrollable : ""
        ].filter(Boolean).join(" ")}
      >
        <Table
          size="small"
          className={classes.partsTable}
          style={{
            tableLayout: "fixed",
            width: "100%",
            minWidth: tableWidth
          }}
        >
          <TableHead
            className={[
              classes.stickyHeader,
              (!stickyHeaderEnabled || isTableScrollContained) ? classes.stickyHeaderDisabled : ""
            ].filter(Boolean).join(" ")}
          >
            <TableRow>
              <StyledHeaderCell
                className={classes.propertyTableHeaderCell}
                style={{ width: ADD_ROW_COLUMN_WIDTH, minWidth: ADD_ROW_COLUMN_WIDTH }}
              />

              <StyledNameHeaderCell
                className={classes.propertyTableHeaderCell}
                style={{
                  width: PROPERTY_PART_NAME_COLUMN_WIDTH,
                  minWidth: PROPERTY_PART_NAME_COLUMN_WIDTH,
                  maxWidth: PROPERTY_PART_NAME_COLUMN_WIDTH
                }}
              >
                <HeaderLabel classes={classes}>
                  {HAKLAI_PROPERTY_PART_LABELS.farmAssets}
                </HeaderLabel>
              </StyledNameHeaderCell>

              <StyledHeaderCell
                className={classes.propertyTableHeaderCell}
                style={{ width: PTOR_COLUMN_WIDTH }}
                aria-label={HAKLAI_PROPERTY_PART_LABELS.requestedPtor}
              >
                <HeaderLabel classes={classes} tooltip={HAKLAI_PROPERTY_PART_TOOLTIPS.requestedPtorForPart}>
                  {HAKLAI_PROPERTY_PART_LABELS.requestedPtor}
                </HeaderLabel>
              </StyledHeaderCell>

              <StyledHeaderCell
                className={classes.propertyTableHeaderCell}
                style={{ width: STANDARD_VALUE_COLUMN_WIDTH }}
              >
                <HeaderLabel classes={classes}>
                  {HAKLAI_PROPERTY_PART_LABELS.relativeSaleValue}
                </HeaderLabel>
              </StyledHeaderCell>

              {showBuiltValueColumn ? (
                <StyledHeaderCell
                  className={classes.propertyTableHeaderCell}
                  style={{ width: STANDARD_VALUE_COLUMN_WIDTH }}
                >
                  <HeaderLabel classes={classes}>
                    {HAKLAI_PROPERTY_PART_LABELS.builtValue}
                  </HeaderLabel>
                </StyledHeaderCell>
              ) : null}

              {showZabanColumn ? (
                <StyledHeaderCell
                  className={classes.propertyTableHeaderCell}
                  style={{ width: zabanColumnWidth }}
                >
                  <HeaderLabel
                    classes={classes}
                    tooltip={HAKLAI_PROPERTY_PART_TOOLTIPS.autoCalculatedWhenZabanSelected}
                  >
                    {HAKLAI_PROPERTY_PART_LABELS.zabanValue}
                  </HeaderLabel>
                </StyledHeaderCell>
              ) : null}

              <StyledHeaderCell
                className={classes.propertyTableHeaderCell}
                style={{ width: DEPRECIATION_COLUMN_WIDTH }}
                aria-label={HAKLAI_PROPERTY_PART_LABELS.rentalDepreciation}
              >
                <HeaderLabel classes={classes} tooltip={HAKLAI_PROPERTY_PART_TOOLTIPS.rentalDepreciationForPart}>
                  {HAKLAI_PROPERTY_PART_LABELS.rentalDepreciation}
                </HeaderLabel>
              </StyledHeaderCell>

              <StyledHeaderCell
                className={classes.propertyTableHeaderCell}
                style={{ width: ACTION_COLUMN_WIDTH, minWidth: ACTION_COLUMN_WIDTH }}
              />
            </TableRow>
          </TableHead>

          {sections.map((section) => (
            <HaklaiPropertySection
              key={section.sectionId}
              classes={classes}
              sectionId={section.sectionId}
              sectionTitle={section.sectionTitle}
              addMode={section.addMode}
              addAriaLabel={section.addAriaLabel}
              onAddClick={section.onAddClick}
              showAddButton={section.showAddButton !== false}
              bodies={section.bodies}
              renderRow={section.renderRow}
              showBuiltValueColumn={showBuiltValueColumn}
              showZabanColumn={showZabanColumn}
            />
          ))}
        </Table>
      </TableContainer>
    </Box>
  );
};

export default React.memo(HaklaiPropertyPartsTable);
